import axios from 'axios'
import type { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import { trackException } from '@/infrastructure/analytics/app-insights'
import { useNotification } from '@/composables/useNotification'

// The auth store and auth service are imported dynamically where they are used, NOT at
// module scope: a static import closes a circular chain (base.service -> http-client
// -> this file -> auth.store -> auth.service extends BaseService), which
// crashes with "Class extends value undefined" whenever the module graph is
// entered via base.service first.

const AUTH_WHITELIST = ['/Account/RenewSession', '/Account/LogOff']

// Set when we send the user to log in. If they come back and requests still fail within
// this window, the session isn't the problem; redirecting again would loop them through
// the login page.
const LOGIN_REDIRECT_KEY = 'tami.sessionExpiredRedirectAt'
const LOGIN_REDIRECT_COOLDOWN_MS = 60_000

function isAuthRequest(error: AxiosError): boolean {
	const requestUrl = error.config?.url ?? ''
	return AUTH_WHITELIST.some((url) => requestUrl.includes(url))
}

function recentlyRedirectedToLogin(): boolean {
	try {
		const at = Number(sessionStorage.getItem(LOGIN_REDIRECT_KEY))
		return Date.now() - at < LOGIN_REDIRECT_COOLDOWN_MS
	} catch {
		return false
	}
}

async function redirectToLogin(): Promise<void> {
	try {
		sessionStorage.setItem(LOGIN_REDIRECT_KEY, String(Date.now()))
	} catch {
		// Storage unavailable: lose the loop guard, never the redirect
	}
	const { useAuthStore } = await import('@/features/auth/stores/auth.store')
	await useAuthStore().redirectToLogin()
}

// One check shared by every request that fails at the same moment (a page load fires several).
let sessionCheck: Promise<boolean | null> | null = null

// true/false is the server's answer; null means the check itself got no answer (offline,
// server down), which must never send anyone to the login page.
function checkSession(): Promise<boolean | null> {
	sessionCheck ??= import('@/features/auth/services/auth.service')
		.then(({ AuthService }) => new AuthService().renewSession())
		.catch(() => null)
		.finally(() => {
			sessionCheck = null
		})
	return sessionCheck
}

// Vue 2 parity (baseSearchService.logException and BulkDownload polling call renewSession()
// when a request fails). An ended session (expired, or cookies cleared) rarely arrives as a
// 401: the backend's default challenge is OIDC, so the call is redirected to the identity
// server on another origin and the browser reports a network error with no response. So
// when a request fails without a response, ask /Account/RenewSession (anonymous, answers
// false once the session is gone) and send the user to log in only if it says so.
async function isEndedSession(error: AxiosError): Promise<boolean> {
	if (error.response || axios.isCancel(error) || isAuthRequest(error)) return false
	if (recentlyRedirectedToLogin()) return false
	return (await checkSession()) === false
}

// Build telemetry properties for a failed axios request. Defensive: every field can
// be undefined for network errors (no response) or malformed errors, so guard all access.
function buildHttpProperties(error: AxiosError): Record<string, unknown> {
	const props: Record<string, unknown> = { source: 'http' }
	try {
		if (error.config?.method) {
			props.method = String(error.config.method).toUpperCase()
		}
		if (error.config?.url) {
			props.url = error.config.url
		}
		if (error.response) {
			props.status = error.response.status
			props.statusText = error.response.statusText
		} else {
			// No response object => network failure, DNS, CORS, or timeout.
			props.status = 'no-response'
		}
		if (error.code) {
			props.code = error.code // e.g. ECONNABORTED for timeouts
		}
	} catch {
		// Never let telemetry-prep break request error propagation.
	}
	return props
}

// Server/network failures are Errors; client (4xx) failures are usually expected
// validation/permission outcomes, so report them at Warning severity for filtering.
// SeverityLevel: 2 = Warning, 3 = Error (Application Insights numeric levels).
function httpSeverity(error: AxiosError): number {
	const status = error.response?.status ?? 0
	return status >= 400 && status < 500 ? 2 : 3
}

export function applyErrorInterceptor(instance: AxiosInstance): void {
	instance.interceptors.response.use(
		(response: AxiosResponse) => response,
		async (error: AxiosError) => {
			// --- Ended session: send the user to log in (Vue 2 parity) ---
			// Not reported to telemetry, like the 401s below.
			if (await isEndedSession(error)) {
				await redirectToLogin()
				// Leave the request pending: the page is unloading, and rejecting would flash
				// an error on screen for as long as the login redirect takes.
				return new Promise<never>(() => {})
			}

			// --- Layer 0: report every HTTP failure to telemetry ---
			// Skip cancellations (navigation away, AbortController) and 401s (owned by the
			// session manager). Reports handled AND unhandled request errors — the broadest
			// net for backend/API failures. `trackException` is a guarded no-op when the
			// App Insights SDK is uninitialised (dev/tests). Always re-rejects below so all
			// existing .catch()/try-catch callers are unaffected, and Layer 2b deliberately
			// skips axios errors to avoid duplicate exceptions in Azure.
			if (!axios.isCancel(error) && error.response?.status !== 401) {
				trackException(error, buildHttpProperties(error), httpSeverity(error))
			}

			if (error.response?.status === 401 && !isAuthRequest(error)) {
				await redirectToLogin()
			}

			// --- 403: friendly toast, never navigate ---
			// Vue 2 (errorHandler.js) surfaces a toast for forbidden responses and does NOT
			// redirect. Many 403s come from gracefully-handled background calls, so yanking the
			// user to a full-page view would be wrong; the /access-denied route is reserved for
			// explicit route-guard denials. Re-reject below so feature-level .catch() still runs.
			if (error.response?.status === 403) {
				useNotification().warning("You don't have access to that resource.")
			}

			return Promise.reject(error)
		},
	)
}
